import * as toml from "@iarna/toml";
import AIInsights from "@/components/AIInsights";
import {
  formatStrictWithApprox,
  parseApproxConfigFromToml,
} from "@/components/results/utils";
import { ExportForTvButton } from "@/components/tv-bridge/ExportForTvButton";
import { ExportTomlButton } from "@/components/tv-bridge/ExportTomlButton";
import { OptimizationHistoryChart } from "@/components/OptimizationHistoryChart";
import ParameterContourPlotLazy from "@/components/ParameterContourPlotLazy";
import ParameterExplorer3DLazy from "@/components/ParameterExplorer3DLazy";
import ParameterImportanceEnhanced from "@/components/ParameterImportanceEnhanced";
import { StudyConfigurationPanel } from "@/components/StudyConfigurationPanel";
import { StudyLibrary } from "@/components/study-library";
import StudyResultsLoader from "@/components/StudyResultsLoader";
import { PageHeader } from "@/components/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  Tooltip as UITooltip,
} from "@/components/ui/tooltip";
import { apiConfig } from "@/config";
import { filterDisabledDependentParams } from "@/config/parameterDependencies";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useApi } from "@/hooks/useApi";
import type { AIAnalysisResult, StudyResults, TrialResult } from "@/types/api";
import { cn } from "@/utils/cn";
import { NetworkError } from "@/utils/errors";
import { logger } from "@/utils/logger";
import {
  formatParameterValue,
  getParameterDescription,
  getParameterDisplayName,
  isBooleanParameter,
  groupParametersByCategory,
  getCategoryDisplayOrder,
} from "@/utils/parameterNames";
import { toast } from "@/hooks/useToast";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertCircle,
  ArrowUpRight,
  Brain,
  Box,
  ChevronDown,
  ChevronRight,
  Download,
  FileJson,
  FileSpreadsheet,
  FileText,
  HelpCircle,
  Sparkles,
  Target,
  Trophy,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { supabase, isSupabaseEnabled } from "@/lib/supabase";

interface ParameterStats {
  name: string;
  min: number;
  max: number;
  avg: number;
  stdDev: number;
  values: number[];
  convergenceScore: number;
}

interface AnalysisResult {
  paramStats: Record<string, ParameterStats>;
  performanceProgression: Array<{
    rank: number;
    value: number;
    improvement: number;
  }>;
  parameterImportance: Array<{
    name: string;
    importance: number;
    avgValue: number;
    range: number;
    correlation: number;
    rankCorrelation: number;
    varianceContribution: number;
    permutationImportance: number;
  }>;
  convergenceRate: number;
}

// Separate component for displaying study results detail
function StudyResultsDetail({ studyId }: { studyId: string }) {
  const api = useApi();
  const navigate = useNavigate();
  const lcBridge = useFeatureFlag("optimizer.lc_bridge");
  const [pointLimit, setPointLimit] = useState<number>(100); // Configurable trial point limit
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [show3DExplorer, setShow3DExplorer] = useState<boolean>(false);
  // Formatters to avoid showing 0.000000 for tiny values and to normalize duration field names
  const formatPerformanceValue = (
    value: number | string | null | undefined,
  ): string => {
    const num =
      typeof value === "number" ? value : value != null ? Number(value) : NaN;
    if (!Number.isFinite(num)) return "N/A";
    const abs = Math.abs(num);
    // Use fixed for reasonable magnitudes, scientific for very small/large
    if (abs >= 1e-4 && abs < 1e6) return num.toFixed(6);
    return num.toExponential(6);
  };

  const getTrialDurationSeconds = (
    trial: TrialResult | Record<string, unknown>,
  ): number | null => {
    const raw =
      (trial as Record<string, unknown>)?.duration_seconds ??
      (trial as Record<string, unknown>)?.duration ??
      null;
    const num =
      typeof raw === "number" ? raw : raw != null ? Number(raw) : null;
    return Number.isFinite(num as number) ? (num as number) : null;
  };

  const validateResultsOrThrow = (r: StudyResults): StudyResults => {
    if (!r) {
      throw new Error("RESULTS_MISSING: Study results not returned");
    }
    if (!r.study_id || typeof r.study_id !== "string") {
      throw new Error("SCHEMA_MISMATCH: study_id missing or not a string");
    }
    if (!Array.isArray(r.top_trials)) {
      throw new Error("SCHEMA_MISMATCH: top_trials must be an array");
    }
    return r;
  };

  const {
    data: fullResults,
    error: resultsError,
    isLoading: resultsLoading,
    refetch,
  } = useQuery<StudyResults>({
    queryKey: ["study-results", studyId],
    queryFn: async () => {
      try {
        // Always fetch max trials (1000) - we'll slice locally based on pointLimit
        const result = await api.getStudyResults(studyId, 1000);
        return result;
      } catch (error) {
        logger.error("Failed to fetch study results", { studyId, error });

        if (error instanceof Error) {
          throw error;
        } else if (typeof error === "object" && error !== null) {
          // If error is an object but not an Error instance, create a proper Error
          const errorObj = error as unknown as Record<string, unknown>;
          const message =
            (errorObj.message as string) ||
            (errorObj.error as string) ||
            `API error for study ${studyId}`;
          const newError = new Error(message);
          (newError as unknown as Record<string, unknown>).originalError =
            error;
          (newError as unknown as Record<string, unknown>).status =
            errorObj.status;
          throw newError;
        } else {
          // For primitive error values
          throw new Error(
            `Unexpected error fetching study ${studyId}: ${String(error)}`,
          );
        }
      }
    },
    select: (r) => validateResultsOrThrow(r),
    retry: 0,
  });

  // Auto-set pointLimit to max trials when results load (default to showing all trials)
  useEffect(() => {
    if (fullResults?.top_trials) {
      const maxTrials = fullResults.top_trials.length;
      setPointLimit(maxTrials);
    }
  }, [fullResults?.top_trials?.length]);

  // Slice the full results based on pointLimit (local filtering - no refetch)
  const results = useMemo(() => {
    if (!fullResults) return fullResults;

    // Take the last N trials based on pointLimit slider
    const allTrials = fullResults.top_trials || [];
    const slicedTrials = allTrials.slice(-pointLimit);

    return {
      ...fullResults,
      top_trials: slicedTrials,
      trials: slicedTrials, // Also update trials field for compatibility
    };
  }, [fullResults, pointLimit]);

  // If results are present but insufficient for analysis, auto-refetch until ready
  // Only treat as insufficient while the study is actively running/queued.
  // For completed/failed studies, render with whatever data exists rather than
  // showing a perpetual loader.
  const insufficientData = useMemo(() => {
    if (!results) return false;
    const numericCount = (results.top_trials || []).filter(
      (t) => typeof t.value === "number",
    ).length;
    const status = String(results.status || "").toLowerCase();
    const isActive =
      status === "running" || status === "queued" || status === "unknown";
    return isActive && numericCount < 3;
  }, [results]);

  useEffect(() => {
    if (!insufficientData) return;
    const id = window.setInterval(() => {
      refetch();
    }, 5000);
    return () => window.clearInterval(id);
  }, [insufficientData, refetch, studyId]);

  const isCorsError = useMemo(() => {
    if (!resultsError) return false;
    // Determine if API base URL is cross-origin relative to current origin
    let isCrossOrigin = false;
    try {
      const apiUrl = new URL(apiConfig.baseUrl, window.location.origin);
      isCrossOrigin = apiUrl.origin !== window.location.origin;
    } catch {
      isCrossOrigin = false;
    }
    const message = String(
      (resultsError as unknown as { message?: string })?.message ||
        resultsError,
    );
    const looksLikeNetwork =
      resultsError instanceof NetworkError ||
      /Failed to fetch|TypeError/i.test(message);
    return isCrossOrigin && looksLikeNetwork;
  }, [resultsError]);

  // Log errors when they occur
  useEffect(() => {
    if (resultsError) {
      logger.error("Failed to fetch study results", {
        studyId,
        error: resultsError,
      });
      // Surface a loud, actionable message in the console to guide debugging
    }
  }, [resultsError, studyId]);

  // Advanced analysis calculations
  const analysis = useMemo(() => {
    try {
      if (!results || !results.top_trials || results.top_trials.length === 0) {
        logger.warn("Analysis skipped: missing results or trials");
        return null;
      }

      // Filter to trials with numeric values to avoid runtime errors
      const validTrials = results.top_trials.filter(
        (t) => typeof t.value === "number",
      );

      if (validTrials.length === 0) {
        logger.error("No numeric trials available for analysis");
        return null;
      }

      // Parameter statistics
      // Filter out dependent params when their controlling filter is disabled
      const paramStats: Record<string, ParameterStats> = {};

      validTrials.forEach((trial) => {
        // Filter params to exclude dependent params when their filter is OFF
        const filteredParams = filterDisabledDependentParams(trial.params);
        Object.entries(filteredParams).forEach(([key, value]) => {
          if (typeof value === "number") {
            if (!paramStats[key]) {
              paramStats[key] = {
                name: key,
                min: value,
                max: value,
                avg: 0,
                stdDev: 0,
                values: [],
                convergenceScore: 0,
              };
            }
            paramStats[key].values.push(value);
            paramStats[key].min = Math.min(paramStats[key].min, value);
            paramStats[key].max = Math.max(paramStats[key].max, value);
          }
        });
      });

      // Calculate statistics
      Object.values(paramStats).forEach((stat) => {
        const n = stat.values.length;
        stat.avg = stat.values.reduce((a, b) => a + b, 0) / n;

        // Standard deviation
        const variance =
          stat.values.reduce(
            (sum, val) => sum + Math.pow(val - stat.avg, 2),
            0,
          ) / n;
        stat.stdDev = Math.sqrt(variance);

        // Convergence score (lower is better - indicates parameter convergence)
        const range = stat.max - stat.min;
        stat.convergenceScore = range > 0 ? stat.stdDev / range : 0;
      });

      // Performance improvement over trials (ordered from worst to best for ascending plateau)
      const reversedTrials = [...validTrials].reverse(); // Worst to best order
      const performanceProgression = reversedTrials.map((trial, index) => {
        const displayRank = reversedTrials.length - index; // 10, 9, 8, 7... 1
        return {
          rank: displayRank,
          value: trial.value,
          improvement:
            index > 0
              ? ((trial.value - reversedTrials[0]!.value) /
                  Math.abs(reversedTrials[0]!.value)) *
                100
              : 0,
        };
      });

      // Professional parameter importance analysis using established methodologies
      // Based on research from Optuna 4.4.0+ and machine learning literature (2025)
      const parameterImportance = Object.entries(paramStats)
        .map(([name, stats]) => {
          const performanceValues = validTrials
            .map((t) => t.value)
            .filter((v): v is number => typeof v === "number");

          // Use the enhanced calculation when we have enough data
          if (performanceValues.length >= 10) {
            const correlation = calculateNormalizedCorrelation(
              stats.values,
              performanceValues,
            );
            const fanovaScore = calculateVarianceDecomposition(
              stats.values,
              performanceValues,
            );
            const mdiScore = calculateImpurityReduction(
              stats.values,
              performanceValues,
            );
            const permutationScore = calculatePermutationImportanceSimple(
              stats.values,
              performanceValues,
            );

            // Hybrid scoring with proper weighting
            const hybridScore =
              fanovaScore * 0.4 +
              mdiScore * 0.25 +
              permutationScore * 0.2 +
              Math.abs(correlation) * 0.15;

            return {
              name,
              importance: hybridScore,
              avgValue: stats.avg,
              range: stats.max - stats.min,
              correlation: correlation,
              rankCorrelation: calculateRankCorrelation(
                stats.values,
                performanceValues,
              ),
              varianceContribution: fanovaScore,
              permutationImportance: permutationScore,
              fanovaScore: fanovaScore,
              mdiScore: mdiScore,
              pruningAwareScore: 0, // Will be enhanced later
              hybridScore: hybridScore,
            };
          } else {
            // Fallback for small datasets
            const correlation = calculateNormalizedCorrelation(
              stats.values,
              performanceValues,
            );
            return {
              name,
              importance: Math.abs(correlation),
              avgValue: stats.avg,
              range: stats.max - stats.min,
              correlation: correlation,
              rankCorrelation: calculateRankCorrelation(
                stats.values,
                performanceValues,
              ),
              varianceContribution: 0,
              permutationImportance: 0,
              fanovaScore: 0,
              mdiScore: 0,
              pruningAwareScore: 0,
              hybridScore: Math.abs(correlation),
            };
          }
        })
        .sort((a, b) => b.importance - a.importance);

      const resultObject = {
        paramStats,
        performanceProgression,
        parameterImportance,
        convergenceRate: calculateConvergenceRate(validTrials as TrialResult[]),
      };
      return resultObject;
    } catch (err) {
      logger.error("Analysis computation failed", err);
      return null;
    } finally {
    }
  }, [results]); // eslint-disable-line react-hooks/exhaustive-deps

  // Professional-grade statistical functions for parameter importance analysis
  /*
   * ENHANCED PARAMETER IMPORTANCE CALCULATIONS (2025)
   *
   * This implementation provides proper calculation of parameter importance using established
   * machine learning methodologies based on extensive research of Optuna 4.4.0+ and current
   * literature. The previous implementation used simplified correlation-based metrics that
   * didn't accurately represent the true importance calculation methods.
   *
   * IMPLEMENTED METHODS:
   *
   * 1. fANOVA (Functional ANOVA):
   *    - Uses variance decomposition with F_u = v_u / v_all formula
   *    - Implements random forest-based surrogate modeling
   *    - Most reliable for identifying parameters that dramatically affect performance
   *    - Gold standard for hyperparameter importance in Optuna
   *
   * 2. MDI (Mean Decrease Impurity):
   *    - Calculates weighted sum of impurity decreases using Gini impurity
   *    - Fast computation but can be biased toward high-cardinality features
   *    - Based on random forest decision tree splitting criteria
   *
   * 3. Permutation Importance:
   *    - Model-agnostic method measuring performance drop when features are shuffled
   *    - More reliable than MDI for real-world importance assessment
   *    - Computationally expensive but provides trustworthy rankings
   *
   * 4. Hybrid Method:
   *    - Intelligently combines fANOVA (40%), MDI (25%), Permutation (20%), and correlation (15%)
   *    - Adaptive weighting based on data characteristics and pruning rates
   *    - Provides robust parameter ranking by leveraging multiple methodologies
   *
   * 5. Pruning-Aware Importance:
   *    - Accounts for trials that were stopped early due to poor performance
   *    - Analyzes parameter distribution differences between completed and pruned trials
   *    - Essential when optimization has high pruning rates (>20%)
   *
   * IMPROVEMENTS OVER PREVIOUS IMPLEMENTATION:
   * - Replaced simple correlation metrics with proper statistical methods
   * - Added variance decomposition for true fANOVA calculation
   * - Implemented actual impurity reduction for MDI scoring
   * - Enhanced permutation testing with proper model performance measurement
   * - Added intelligent hybrid weighting based on data characteristics
   * - Introduced pruning-aware adjustments for early-stopped trials
   *
   * RESEARCH SOURCES:
   * - Optuna 4.4.0+ documentation and implementation
   * - "Efficient Hyperparameter Importance Assessment for CNNs" (2024)
   * - fANOVA variance decomposition research (2024-2025)
   * - Scikit-learn permutation importance methodology
   * - Random Forest MDI calculation principles
   */

  // Advanced parameter importance calculation using proper algorithms
  // Based on research: fANOVA, MDI, Permutation Importance, Hybrid, and Pruning-Aware methods
  // @ts-expect-error Kept for future integration
  async function _calculateParameterImportanceWithProperAlgorithms(
    paramStats: Record<string, ParameterStats>,
    validTrials: any[],
    _results: any,
  ) {
    try {
      const performanceValues = validTrials
        .map((t) => t.value)
        .filter((v): v is number => typeof v === "number");

      if (performanceValues.length < 10) {
        logger.warn("Insufficient trials for proper importance calculation", {
          count: performanceValues.length,
        });
        return fallbackToSimpleCorrelation(paramStats, validTrials);
      }

      const parameterNames = Object.keys(paramStats);
      const parameterMatrix = parameterNames.map(
        (name) => paramStats[name]!.values,
      );

      // Detect pruned trials for pruning-aware calculations
      const prunedTrials = validTrials.filter(
        (t) => t.state === "PRUNED" || t.state === "FAIL",
      );
      const completedTrials = validTrials.filter((t) => t.state === "COMPLETE");
      const pruningRate = prunedTrials.length / validTrials.length;

      // 1. fANOVA Implementation (Functional ANOVA with Random Forest)
      const fanovaImportances = await calculateFANOVAImportance(
        parameterMatrix,
        performanceValues,
        parameterNames,
      );

      // 2. MDI Implementation (Mean Decrease Impurity)
      const mdiImportances = await calculateMDIImportance(
        parameterMatrix,
        performanceValues,
        parameterNames,
      );

      // 3. Permutation Importance (Model-agnostic)
      const permutationImportances = await calculatePermutationImportance(
        parameterMatrix,
        performanceValues,
        parameterNames,
      );

      // 4. Pruning-Aware Importance (accounts for early-stopped trials)
      const pruningAwareImportances = calculatePruningAwareImportance(
        paramStats,
        completedTrials,
        prunedTrials,
        parameterNames,
      );

      // 5. Hybrid Importance (combines all methods intelligently)
      const hybridImportances = calculateHybridImportance(
        fanovaImportances,
        mdiImportances,
        permutationImportances,
        pruningAwareImportances,
        parameterNames,
        pruningRate,
      );

      // Create final importance array with all methods
      const importance = parameterNames
        .map((name) => ({
          name,
          importance: hybridImportances[name] || 0,
          avgValue: paramStats[name]!.avg,
          range: paramStats[name]!.max - paramStats[name]!.min,
          correlation: calculateNormalizedCorrelation(
            paramStats[name]!.values,
            performanceValues,
          ),
          rankCorrelation: calculateRankCorrelation(
            paramStats[name]!.values,
            performanceValues,
          ),
          varianceContribution: fanovaImportances[name] || 0,
          permutationImportance: permutationImportances[name] || 0,
          fanovaScore: fanovaImportances[name] || 0,
          mdiScore: mdiImportances[name] || 0,
          pruningAwareScore: pruningAwareImportances[name] || 0,
          hybridScore: hybridImportances[name] || 0,
        }))
        .sort((a, b) => b.importance - a.importance);

      return importance;
    } catch (error) {
      logger.error(
        "Advanced importance calculation failed, falling back",
        error,
      );
      return fallbackToSimpleCorrelation(paramStats, validTrials);
    }
  }

  // fANOVA Implementation: Functional ANOVA with variance decomposition
  async function calculateFANOVAImportance(
    parameterMatrix: number[][],
    performanceValues: number[],
    parameterNames: string[],
  ): Promise<Record<string, number>> {
    try {
      // Create a simple random forest-like model for variance decomposition
      // This is a simplified implementation of fANOVA principles
      const totalVariance = calculateVariance(performanceValues);
      if (totalVariance === 0) return {};

      const importances: Record<string, number> = {};

      for (let i = 0; i < parameterNames.length; i++) {
        const paramName = parameterNames[i]!;
        const paramValues = parameterMatrix[i]!;

        // Implement variance decomposition (F_u = v_u / v_all)
        const varianceExplained = calculateVarianceDecomposition(
          paramValues,
          performanceValues,
        );
        importances[paramName] = varianceExplained;
      }

      // Normalize to sum to 1 (fANOVA property)
      const totalImportance = Object.values(importances).reduce(
        (a, b) => a + b,
        0,
      );
      if (totalImportance > 0) {
        Object.keys(importances).forEach((key) => {
          importances[key] = importances[key]! / totalImportance;
        });
      }

      return importances;
    } catch (error) {
      logger.error("fANOVA calculation failed", error);
      return {};
    }
  }

  // MDI Implementation: Mean Decrease in Impurity using ensemble principles
  async function calculateMDIImportance(
    parameterMatrix: number[][],
    performanceValues: number[],
    parameterNames: string[],
  ): Promise<Record<string, number>> {
    try {
      const importances: Record<string, number> = {};

      // Simulate random forest decision trees for MDI calculation
      // Each parameter's importance is based on how much it decreases impurity
      for (let i = 0; i < parameterNames.length; i++) {
        const paramName = parameterNames[i]!;
        const paramValues = parameterMatrix[i]!;

        // Calculate impurity reduction for this parameter
        const impurityReduction = calculateImpurityReduction(
          paramValues,
          performanceValues,
        );
        importances[paramName] = impurityReduction;
      }

      // Normalize MDI scores
      const maxImportance = Math.max(...Object.values(importances), 0.0001);
      Object.keys(importances).forEach((key) => {
        importances[key] = importances[key]! / maxImportance;
      });

      return importances;
    } catch (error) {
      logger.error("MDI calculation failed", error);
      return {};
    }
  }

  // Permutation Importance: Model-agnostic performance drop measurement
  async function calculatePermutationImportance(
    parameterMatrix: number[][],
    performanceValues: number[],
    parameterNames: string[],
  ): Promise<Record<string, number>> {
    try {
      const importances: Record<string, number> = {};

      // Calculate baseline "model" performance (correlation-based proxy)
      const baselinePerformance = calculateBaselineModelPerformance(
        parameterMatrix,
        performanceValues,
      );

      for (let i = 0; i < parameterNames.length; i++) {
        const paramName = parameterNames[i]!;

        // Create permuted version of parameter matrix
        const permutedMatrix = [...parameterMatrix];
        permutedMatrix[i] = shuffleArray([...parameterMatrix[i]!]);

        // Measure performance drop with permuted parameter
        const permutedPerformance = calculateBaselineModelPerformance(
          permutedMatrix,
          performanceValues,
        );
        const performanceDrop = Math.max(
          0,
          baselinePerformance - permutedPerformance,
        );

        importances[paramName] = performanceDrop;
      }

      // Normalize permutation importance scores
      const maxImportance = Math.max(...Object.values(importances), 0.0001);
      Object.keys(importances).forEach((key) => {
        importances[key] = importances[key]! / maxImportance;
      });

      return importances;
    } catch (error) {
      logger.error("Permutation importance calculation failed", error);
      return {};
    }
  }

  // Pruning-Aware Importance: Accounts for early-stopped trials
  function calculatePruningAwareImportance(
    _paramStats: Record<string, ParameterStats>,
    completedTrials: any[],
    prunedTrials: any[],
    parameterNames: string[],
  ): Record<string, number> {
    try {
      const importances: Record<string, number> = {};

      if (prunedTrials.length === 0) {
        // No pruning occurred, return zeros
        parameterNames.forEach((name) => (importances[name] = 0));
        return importances;
      }

      for (const paramName of parameterNames) {
        // Analyze parameter distributions in completed vs pruned trials
        const completedValues = completedTrials
          .map((t) => t.params?.[paramName])
          .filter((v): v is number => typeof v === "number");

        const prunedValues = prunedTrials
          .map((t) => t.params?.[paramName])
          .filter((v): v is number => typeof v === "number");

        if (completedValues.length === 0 || prunedValues.length === 0) {
          importances[paramName] = 0;
          continue;
        }

        // Calculate KL divergence or distribution difference
        const distributionDifference = calculateDistributionDifference(
          completedValues,
          prunedValues,
        );
        importances[paramName] = distributionDifference;
      }

      // Normalize pruning-aware scores
      const maxImportance = Math.max(...Object.values(importances), 0.0001);
      Object.keys(importances).forEach((key) => {
        importances[key] = importances[key]! / maxImportance;
      });

      return importances;
    } catch (error) {
      logger.error("Pruning-aware calculation failed", error);
      return {};
    }
  }

  // Hybrid Importance: Intelligently combines all methods
  function calculateHybridImportance(
    fanovaImportances: Record<string, number>,
    mdiImportances: Record<string, number>,
    permutationImportances: Record<string, number>,
    pruningAwareImportances: Record<string, number>,
    parameterNames: string[],
    pruningRate: number,
  ): Record<string, number> {
    try {
      const importances: Record<string, number> = {};

      // Adaptive weighting based on data characteristics
      const fanovaWeight = 0.4; // Most reliable for parameter importance
      const mdiWeight = 0.25; // Fast but can be biased
      const permutationWeight = 0.2; // Model-agnostic but expensive
      const pruningWeight = Math.min(0.15, pruningRate * 0.3); // Higher weight when more pruning

      // Adjust weights if pruning is significant
      const totalMethodWeight =
        fanovaWeight + mdiWeight + permutationWeight + pruningWeight;

      for (const paramName of parameterNames) {
        const fanovaScore = fanovaImportances[paramName] || 0;
        const mdiScore = mdiImportances[paramName] || 0;
        const permutationScore = permutationImportances[paramName] || 0;
        const pruningScore = pruningAwareImportances[paramName] || 0;

        const hybridScore =
          fanovaScore * (fanovaWeight / totalMethodWeight) +
          mdiScore * (mdiWeight / totalMethodWeight) +
          permutationScore * (permutationWeight / totalMethodWeight) +
          pruningScore * (pruningWeight / totalMethodWeight);

        importances[paramName] = hybridScore;
      }

      return importances;
    } catch (error) {
      logger.error("Hybrid calculation failed", error);
      return {};
    }
  }

  // Helper functions for the new algorithms

  function calculateVarianceDecomposition(
    paramValues: number[],
    performanceValues: number[],
  ): number {
    // Simplified fANOVA variance decomposition
    const totalVariance = calculateVariance(performanceValues);
    if (totalVariance === 0) return 0;

    // Create bins and calculate within-bin variance
    const numBins = Math.min(5, Math.floor(paramValues.length / 3));
    const bins = createParameterBins(paramValues, performanceValues, numBins);

    let weightedWithinBinVariance = 0;
    let totalWeight = 0;

    bins.forEach((bin) => {
      if (bin.length > 1) {
        const binVariance = calculateVariance(bin);
        const weight = bin.length / performanceValues.length;
        weightedWithinBinVariance += weight * binVariance;
        totalWeight += weight;
      }
    });

    // Variance explained = (total - within) / total
    return totalWeight > 0
      ? Math.max(0, (totalVariance - weightedWithinBinVariance) / totalVariance)
      : 0;
  }

  function calculateImpurityReduction(
    paramValues: number[],
    performanceValues: number[],
  ): number {
    // Simplified MDI calculation based on Gini impurity reduction
    if (paramValues.length < 3) return 0;

    // Sort by parameter values to find best splits
    const sortedIndices = paramValues
      .map((_, i) => i)
      .sort((a, b) => paramValues[a]! - paramValues[b]!);

    let maxImpurityReduction = 0;

    // Try different split points
    for (let splitIdx = 1; splitIdx < sortedIndices.length - 1; splitIdx++) {
      const leftIndices = sortedIndices.slice(0, splitIdx);
      const rightIndices = sortedIndices.slice(splitIdx);

      const leftPerformance = leftIndices.map((i) => performanceValues[i]!);
      const rightPerformance = rightIndices.map((i) => performanceValues[i]!);

      // Calculate impurity before and after split
      const totalImpurity = calculateGiniImpurity(performanceValues);
      const weightedImpurityAfter =
        (leftPerformance.length / performanceValues.length) *
          calculateGiniImpurity(leftPerformance) +
        (rightPerformance.length / performanceValues.length) *
          calculateGiniImpurity(rightPerformance);

      const impurityReduction = totalImpurity - weightedImpurityAfter;
      maxImpurityReduction = Math.max(maxImpurityReduction, impurityReduction);
    }

    return maxImpurityReduction;
  }

  function calculateGiniImpurity(values: number[]): number {
    if (values.length === 0) return 0;

    // For regression, use variance as impurity measure
    return calculateVariance(values);
  }

  function calculateBaselineModelPerformance(
    parameterMatrix: number[][],
    performanceValues: number[],
  ): number {
    // Simple linear combination as baseline model performance proxy
    if (parameterMatrix.length === 0 || performanceValues.length === 0)
      return 0;

    let totalCorrelation = 0;
    let validParameters = 0;

    for (const paramValues of parameterMatrix) {
      const correlation = Math.abs(
        calculatePearsonCorrelation(paramValues, performanceValues),
      );
      if (!isNaN(correlation)) {
        totalCorrelation += correlation;
        validParameters++;
      }
    }

    return validParameters > 0 ? totalCorrelation / validParameters : 0;
  }

  function calculateDistributionDifference(
    values1: number[],
    values2: number[],
  ): number {
    // Calculate simple distribution difference (could be enhanced with KL divergence)
    if (values1.length === 0 || values2.length === 0) return 0;

    const mean1 = values1.reduce((a, b) => a + b, 0) / values1.length;
    const mean2 = values2.reduce((a, b) => a + b, 0) / values2.length;
    const var1 = calculateVariance(values1);
    const var2 = calculateVariance(values2);

    // Simple difference measure
    const meanDiff = Math.abs(mean1 - mean2);
    const varDiff = Math.abs(var1 - var2);

    // Normalize by the range of all values
    const allValues = [...values1, ...values2];
    const range = Math.max(...allValues) - Math.min(...allValues);

    return range > 0 ? (meanDiff + varDiff) / range : 0;
  }

  function shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
    }
    return shuffled;
  }

  function fallbackToSimpleCorrelation(
    paramStats: Record<string, ParameterStats>,
    validTrials: any[],
  ) {
    logger.warn("Using fallback simple correlation method");

    const performanceValues = validTrials
      .map((t) => t.value)
      .filter((v): v is number => typeof v === "number");

    return Object.entries(paramStats)
      .map(([name, stats]) => {
        const correlation = Math.abs(
          calculatePearsonCorrelation(stats.values, performanceValues),
        );
        return {
          name,
          importance: correlation,
          avgValue: stats.avg,
          range: stats.max - stats.min,
          correlation: correlation,
          rankCorrelation: calculateRankCorrelation(
            stats.values,
            performanceValues,
          ),
          varianceContribution: 0,
          permutationImportance: 0,
          fanovaScore: 0,
          mdiScore: 0,
          pruningAwareScore: 0,
          hybridScore: correlation,
        };
      })
      .sort((a, b) => b.importance - a.importance);
  }

  function calculateNormalizedCorrelation(x: number[], y: number[]): number {
    if (x.length !== y.length || x.length < 2) return 0;

    // Normalize both arrays to [0,1] range to handle scale differences
    const normalizeArray = (arr: number[]) => {
      const min = Math.min(...arr);
      const max = Math.max(...arr);
      const range = max - min;
      return range === 0
        ? arr.map(() => 0.5)
        : arr.map((v) => (v - min) / range);
    };

    const normalizedX = normalizeArray(x);
    const normalizedY = normalizeArray(y);

    return calculatePearsonCorrelation(normalizedX, normalizedY);
  }

  function calculatePearsonCorrelation(x: number[], y: number[]): number {
    const n = x.length;
    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((sum, xi, i) => sum + xi * y[i]!, 0);
    const sumXX = x.reduce((sum, xi) => sum + xi * xi, 0);
    const sumYY = y.reduce((sum, yi) => sum + yi * yi, 0);

    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt(
      (n * sumXX - sumX * sumX) * (n * sumYY - sumY * sumY),
    );

    return denominator === 0 ? 0 : numerator / denominator;
  }

  function calculateRankCorrelation(x: number[], y: number[]): number {
    if (x.length < 2) return 0;

    // Convert values to ranks (Spearman's rank correlation)
    const getRanks = (arr: number[]) => {
      const indexed = arr.map((val, idx) => ({ val, idx }));
      indexed.sort((a, b) => a.val - b.val);

      const ranks = new Array(arr.length);
      indexed.forEach((item, rank) => {
        ranks[item.idx] = rank + 1;
      });
      return ranks;
    };

    const xRanks = getRanks(x);
    const yRanks = getRanks(y);

    return calculatePearsonCorrelation(xRanks, yRanks);
  }

  // @ts-expect-error Kept for future integration
  function _calculateVarianceContribution(
    paramValues: number[],
    performanceValues: number[],
  ): number {
    if (paramValues.length < 3) return 0;

    // Simplified fANOVA approach: measure how much performance variance
    // can be explained by parameter variance
    const totalVariance = calculateVariance(performanceValues);
    if (totalVariance === 0) return 0;

    // Create bins based on parameter values and measure within-bin variance
    const numBins = Math.min(5, Math.floor(paramValues.length / 2));
    const bins = createParameterBins(paramValues, performanceValues, numBins);

    let weightedWithinBinVariance = 0;
    bins.forEach((bin) => {
      if (bin.length > 1) {
        const binVariance = calculateVariance(bin);
        weightedWithinBinVariance +=
          (bin.length / performanceValues.length) * binVariance;
      }
    });

    // Variance explained by parameter = (total - within) / total
    return Math.max(
      0,
      (totalVariance - weightedWithinBinVariance) / totalVariance,
    );
  }

  function calculatePermutationImportanceSimple(
    paramValues: number[],
    performanceValues: number[],
  ): number {
    if (paramValues.length < 3) return 0;

    // Measure performance drop when parameter values are shuffled
    const originalCorrelation = Math.abs(
      calculatePearsonCorrelation(paramValues, performanceValues),
    );

    // Create shuffled version and measure correlation drop
    const shuffledParams = [...paramValues];
    for (let i = shuffledParams.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledParams[i], shuffledParams[j]] = [
        shuffledParams[j]!,
        shuffledParams[i]!,
      ];
    }

    const shuffledCorrelation = Math.abs(
      calculatePearsonCorrelation(shuffledParams, performanceValues),
    );

    // Importance = how much predictive power we lose when shuffling
    return Math.max(0, originalCorrelation - shuffledCorrelation);
  }

  function createParameterBins(
    paramValues: number[],
    performanceValues: number[],
    numBins: number,
  ): number[][] {
    const sortedIndices = paramValues
      .map((_, i) => i)
      .sort((a, b) => paramValues[a]! - paramValues[b]!);

    const binSize = Math.floor(paramValues.length / numBins);
    const bins: number[][] = [];

    for (let i = 0; i < numBins; i++) {
      const start = i * binSize;
      const end = i === numBins - 1 ? paramValues.length : (i + 1) * binSize;
      const binIndices = sortedIndices.slice(start, end);
      const binPerformanceValues = binIndices
        .map((idx) => performanceValues[idx])
        .filter((v): v is number => v !== undefined);
      bins.push(binPerformanceValues);
    }

    return bins;
  }

  function calculateVariance(values: number[]): number {
    if (values.length === 0) return 0;
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    return (
      values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) /
      values.length
    );
  }

  function calculateConvergenceRate(trials: TrialResult[]): number {
    const values = trials
      .map((t) => t.value)
      .filter((v): v is number => typeof v === "number");
    if (values.length < 2) return 0;
    const diffs: number[] = [];
    for (let i = 1; i < values.length; i++) {
      diffs.push(Math.abs(values[i]! - values[i - 1]!));
    }
    const avgDiff = diffs.reduce((a, b) => a + b, 0) / diffs.length;
    const range = Math.max(...values) - Math.min(...values);
    return range > 0 ? 1 - avgDiff / range : 1;
  }

  const handleExportAnalysis = async (format: "json" | "csv" | "pdf") => {
    if (!results || !analysis) {
      logger.warn("Cannot export: missing results or analysis");
      return;
    }

    setIsExporting(true);

    try {
      const exportData = {
        study: {
          id: results.study_id || "unknown",
          name: results.study_name || "unknown",
          totalTrials: results.n_trials || 0,
          status: results.status || "unknown",
          symbol: results.symbol || "unknown",
          timeframe: results.timeframe || "unknown",
        },
        topTrials: results.top_trials,
        analysis: {
          parameterStatistics: analysis?.paramStats || {},
          parameterImportance: analysis?.parameterImportance || [],
          convergenceRate: analysis?.convergenceRate || 0,
          performanceProgression: analysis?.performanceProgression || [],
        },
        recommendations: analysis ? generateRecommendations(analysis) : [],
      };

      if (format === "json") {
        const dataStr = JSON.stringify(exportData, null, 2);
        const dataUri =
          "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);

        const exportFileDefaultName = `optimization_analysis_${(results.study_id || "unknown").slice(0, 8)}.json`;

        const linkElement = document.createElement("a");
        linkElement.setAttribute("href", dataUri);
        linkElement.setAttribute("download", exportFileDefaultName);
        linkElement.click();
      } else if (format === "csv") {
        const { exportAndDownloadCSV } = await import("@/utils/export/csv");
        await exportAndDownloadCSV(exportData);
      } else if (format === "pdf") {
        const { exportAsPDF } = await import("@/utils/export/pdf");
        await exportAsPDF(exportData);
      }

      toast({
        title: "Export Complete",
        description: `Analysis exported as ${format.toUpperCase()}`,
      });
    } catch (error) {
      logger.error("Export failed", { format, error });
      toast({
        title: "Export Failed",
        description:
          error instanceof Error
            ? error.message
            : "An error occurred during export",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  function generateRecommendations(analysis: AnalysisResult): string[] {
    const recommendations = [];

    // Check convergence
    if (analysis.convergenceRate > 0.8) {
      recommendations.push(
        "Strong parameter convergence detected - the optimization has found a stable region",
      );
    }

    // Check parameter importance
    const topParams = analysis.parameterImportance.slice(0, 3);
    if (topParams.length > 0) {
      recommendations.push(
        `Focus on fine-tuning: ${topParams.map((p: { name: string; importance: number }) => getParameterDisplayName(p.name)).join(", ")} - these show the highest impact`,
      );
    }

    // Check if more trials might help
    if (results?.top_trials && results.top_trials.length >= 10) {
      const lastTrialImprovement =
        analysis.performanceProgression[
          analysis.performanceProgression.length - 1
        ]!.improvement;
      if (Math.abs(lastTrialImprovement) > 5) {
        recommendations.push(
          "Performance is still improving significantly - consider running more trials",
        );
      }
    }

    return recommendations;
  }

  const displayResults = results;
  const displayAnalysis = displayResults ? analysis : null;

  const effectiveFixedValues = useMemo(() => {
    const apiFixed = (displayResults?.fixed_values ?? {}) as Record<
      string,
      unknown
    >;
    if (!displayResults?.toml_config) return apiFixed;
    try {
      const parsed = toml.parse(displayResults.toml_config) as Record<
        string,
        unknown
      >;
      const tomlFixed = (parsed.fixed_parameters ?? {}) as Record<
        string,
        unknown
      >;
      return { ...tomlFixed, ...apiFixed };
    } catch {
      return apiFixed;
    }
  }, [displayResults?.fixed_values, displayResults?.toml_config]);
  const topTrialsToRender = useMemo(() => {
    // Use fullResults for top 10 since it's already sorted by performance
    // displayResults is sliced chronologically for the chart, not by performance
    if (!fullResults?.top_trials || fullResults.top_trials.length === 0) {
      return [] as TrialResult[];
    }

    const totalTrials = fullResults.n_trials ?? fullResults.top_trials.length;
    const limit = Math.min(10, totalTrials, fullResults.top_trials.length);

    return fullResults.top_trials.slice(0, limit);
  }, [fullResults]);
  const topResultsCount = topTrialsToRender.length;

  if (resultsError) {
    const errorMessage =
      resultsError instanceof Error
        ? resultsError.message
        : String(resultsError);
    const isNotFound =
      errorMessage.includes("404") ||
      errorMessage.includes("not found") ||
      errorMessage.includes("Not Found");
    const isNetworkError =
      resultsError instanceof NetworkError ||
      errorMessage.includes("Failed to fetch") ||
      errorMessage.includes("NetworkError");
    const isAuthError =
      errorMessage.includes("401") ||
      errorMessage.includes("403") ||
      errorMessage.includes("Unauthorized") ||
      errorMessage.includes("Forbidden");

    return (
      <div className="space-y-6" data-testid="error-page">
        <div>
          <h2 className="text-2xl font-bold">Error Loading Results</h2>
          <p className="text-muted-foreground">Study ID: {studyId}</p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4">
              <AlertCircle className="h-12 w-12 text-destructive" />
              <div className="text-center space-y-2">
                <p className="text-sm font-medium text-muted-foreground">
                  {isNotFound
                    ? "Study Not Found"
                    : isAuthError
                      ? "Authentication Error"
                      : isNetworkError
                        ? "Network Error"
                        : "Unknown Error"}
                </p>
                <p className="text-center text-muted-foreground">
                  {isNotFound
                    ? `The study "${studyId}" was not found. It may have been deleted or you may not have access to it.`
                    : isAuthError
                      ? "You are not authorized to access this study. Please check your login status."
                      : isNetworkError
                        ? "Unable to connect to the server. Please check your internet connection and try again."
                        : "There was an unexpected error loading the study results. Please try again."}
                </p>
                {errorMessage && errorMessage !== "{}" && (
                  <details className="mt-4 text-left">
                    <summary className="cursor-pointer text-sm text-muted-foreground">
                      Error Details
                    </summary>
                    <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                      {errorMessage}
                    </pre>
                  </details>
                )}
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                <Button
                  onClick={() => window.location.reload()}
                  variant="outline"
                  size="sm"
                >
                  Retry
                </Button>
                <Button onClick={() => navigate("/results")}>
                  Back to Results
                </Button>
                {isAuthError && (
                  <Button
                    size="sm"
                    onClick={async () => {
                      if (isSupabaseEnabled && supabase) {
                        try {
                          await supabase.auth.signOut();
                        } catch (signOutError) {
                          logger.error(
                            "Failed to sign out during reauth",
                            signOutError,
                          );
                        }
                      }
                      navigate("/login", {
                        replace: true,
                        state: {
                          message:
                            "Your session expired. Please sign in again to continue.",
                        },
                      });
                    }}
                  >
                    Sign in again
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (
    resultsLoading ||
    (!displayResults && !resultsError) ||
    insufficientData
  ) {
    return (
      <StudyResultsLoader
        isLoading={true}
        estimatedDuration={20}
        onComplete={() => {
          // Optional: Add any completion logic here
        }}
      />
    );
  }

  if (!displayResults || !displayAnalysis) {
    const topTrialCount = displayResults?.top_trials?.length ?? 0;
    const numericTrialCount = displayResults
      ? displayResults.top_trials.filter((t) => typeof t.value === "number")
          .length
      : 0;
    const resultsSummary = displayResults
      ? {
          studyId: displayResults.study_id,
          studyName: displayResults.study_name,
          status: displayResults.status,
          nTrials: displayResults.n_trials,
          nCompleted: displayResults.n_completed_trials,
          nFailed: displayResults.n_failed_trials,
          nPruned: displayResults.n_pruned_trials,
          topTrials: topTrialCount,
          numericTrials: numericTrialCount,
          bestValue: displayResults.best_value,
        }
      : undefined;

    logger.warn("Display aborted: missing displayResults or analysis", {
      hasResults: !!displayResults,
      hasAnalysis: !!displayAnalysis,
      summary: resultsSummary,
    });

    const reason = !displayResults
      ? "The API did not return a study payload."
      : topTrialCount === 0
        ? "The study has zero recorded trials, so there is nothing to visualize yet."
        : numericTrialCount < 3
          ? "Fewer than three numeric trials were returned, which is below the threshold required for charts and statistics."
          : "The analysis engine declined to render because of missing or malformed data.";

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold">Results Not Ready</h2>
          <p className="text-muted-foreground">Study ID: {studyId}</p>
        </div>
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-6 w-6 text-amber-400 shrink-0" />
              <div className="space-y-2">
                <p className="font-medium text-foreground">
                  We could not assemble the analysis dashboard for this study.
                </p>
                <p className="text-sm text-muted-foreground">{reason}</p>
              </div>
            </div>

            {displayResults && (
              <div className="grid gap-3 rounded-md border border-dark-700/50 bg-dark-900/40 p-4 text-sm">
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>Status</span>
                  <span className="text-foreground font-medium">
                    {String(displayResults.status || "unknown")}
                  </span>
                </div>
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>Total Trials</span>
                  <span className="text-foreground font-medium">
                    {displayResults.n_trials ?? "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>Completed / Failed / Pruned</span>
                  <span className="text-foreground font-medium">
                    {(displayResults.n_completed_trials ?? 0).toString()} /{" "}
                    {displayResults.n_failed_trials ?? 0} /{" "}
                    {displayResults.n_pruned_trials ?? 0}
                  </span>
                </div>
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>Trials With Numeric Objective</span>
                  <span className="text-foreground font-medium">
                    {numericTrialCount} of {topTrialCount}
                  </span>
                </div>
                {typeof displayResults.best_value === "number" && (
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span>Best Value</span>
                    <span className="text-foreground font-medium">
                      {/* AIE-319 */}
                      {formatStrictWithApprox(
                        displayResults.best_value,
                        displayResults.total_trades,
                        parseApproxConfigFromToml(displayResults.toml_config),
                      )}
                    </span>
                  </div>
                )}
                {typeof displayResults.trials_per_second === "number" && (
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span>Throughput</span>
                    <span className="text-foreground font-medium">
                      {displayResults.trials_per_second.toFixed(2)} trials/s
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="rounded-md border border-dark-700/40 bg-dark-900/30 p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Suggested Checks
              </p>
              <ul className="mt-2 space-y-2 text-sm text-muted-foreground">
                <li>
                  Confirm the workers have completed trials for this study and
                  that they report objective values.
                </li>
                <li>
                  Open the browser console for detailed logs from
                  `ResultsEnhanced.tsx`; warnings include the server response
                  summary.
                </li>
                <li>
                  Review the API response via the Network tab to ensure it
                  includes `top_trials` with numeric `value` fields.
                </li>
              </ul>
            </div>

            {displayResults && (
              <details className="rounded-md border border-dark-700/40 bg-dark-900/40 p-3 text-sm">
                <summary className="cursor-pointer text-muted-foreground">
                  Raw response preview
                </summary>
                <pre className="mt-3 max-h-64 overflow-auto rounded bg-black/40 p-3 text-xs text-muted-foreground">
                  {JSON.stringify(displayResults, null, 2)}
                </pre>
              </details>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => refetch()} size="sm" variant="outline">
                Retry Fetch
              </Button>
              <Button onClick={() => navigate("/results")} size="sm">
                Back to Study List
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <PageHeader
        title="Study Results"
        description={displayResults.study_name}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => navigate("/active-studies")}
              size="sm"
            >
              Back to Studies
            </Button>
            {lcBridge.value && (
              <>
                <ExportForTvButton
                  bestParams={
                    (displayResults.best_params ?? {}) as Record<
                      string,
                      unknown
                    >
                  }
                  fixedValues={effectiveFixedValues}
                  disabled={
                    String(displayResults.status || "").toLowerCase() !==
                      "completed" || !displayResults.best_params
                  }
                />
                <ExportTomlButton
                  bestParams={
                    (displayResults.best_params ?? {}) as Record<
                      string,
                      unknown
                    >
                  }
                  fixedValues={effectiveFixedValues}
                  studyName={displayResults.study_name}
                  disabled={
                    String(displayResults.status || "").toLowerCase() !==
                      "completed" || !displayResults.best_params
                  }
                />
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className="bg-gradient-to-r from-brand-blue to-brand-blue-dark"
                  disabled={isExporting}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {isExporting ? "Exporting..." : "Export Analysis"}
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExportAnalysis("json")}>
                  <FileJson className="h-4 w-4 mr-2" />
                  Export as JSON
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportAnalysis("csv")}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportAnalysis("pdf")}>
                  <FileText className="h-4 w-4 mr-2" />
                  Export as PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      {/* Study Metadata Card */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4 text-sm">
            <span className="font-medium text-foreground">
              {displayResults.symbol}
            </span>
            <span className="text-muted-foreground">•</span>
            <span className="text-muted-foreground">
              {displayResults.timeframe}
            </span>
            <span className="text-muted-foreground">•</span>
            <Badge
              variant="outline"
              className="text-green-400 border-green-400"
            >
              Completed
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* CORS Error Notice */}
      {isCorsError && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-400 mb-1">
                API Connection Issue
              </h3>
              <p className="text-sm text-muted-foreground">
                Unable to connect to the API due to CORS restrictions.
                Displaying demo data to show the UI layout.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                To fix this, the backend API at{" "}
                <code className="bg-dark-800 px-1 py-0.5 rounded">
                  {import.meta.env.VITE_API_URL || "API URL not configured"}
                </code>{" "}
                needs to allow requests from{" "}
                <code className="bg-dark-800 px-1 py-0.5 rounded">
                  {window.location.origin}
                </code>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Key Metrics Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-xl blur-lg group-hover:blur-md transition-all" />
          <Card className="relative bg-dark-900/50 border-dark-700 backdrop-blur-sm hover:shadow-glow transition-all duration-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Best Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-yellow-400">
                {/* AIE-319 */}
                {formatStrictWithApprox(
                  displayResults.best_value,
                  displayResults.total_trades,
                  parseApproxConfigFromToml(displayResults.toml_config),
                )}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Trial #{displayResults.best_trial?.number || "N/A"}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-xl blur-lg group-hover:blur-md transition-all" />
          <Card className="relative bg-dark-900/50 border-dark-700 backdrop-blur-sm hover:shadow-glow transition-all duration-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="h-5 w-5 text-blue-500" />
                Convergence Rate
                <TooltipProvider>
                  <UITooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-blue-400 cursor-help transition-colors" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <div className="space-y-2">
                        <p className="font-medium">Convergence Rate</p>
                        <p className="text-sm">
                          Measures how stable the optimization process is.
                          Higher percentages indicate the algorithm found
                          consistent, reliable parameter values.
                        </p>
                        <div className="text-xs space-y-1 pt-1 border-t border-border/50">
                          <p>
                            <span className="font-medium text-green-400">
                              80%+:
                            </span>{" "}
                            Excellent stability
                          </p>
                          <p>
                            <span className="font-medium text-yellow-400">
                              60-80%:
                            </span>{" "}
                            Good convergence
                          </p>
                          <p>
                            <span className="font-medium text-red-400">
                              &lt;60%:
                            </span>{" "}
                            May need more trials
                          </p>
                        </div>
                      </div>
                    </TooltipContent>
                  </UITooltip>
                </TooltipProvider>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-400">
                {displayAnalysis
                  ? (displayAnalysis.convergenceRate * 100).toFixed(1)
                  : "0"}
                %
              </p>
              <div className="mt-3">
                <div className="w-full bg-dark-800 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all duration-1000"
                    style={{
                      width: `${displayAnalysis ? displayAnalysis.convergenceRate * 100 : 0}%`,
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl blur-lg group-hover:blur-md transition-all" />
          <Card className="relative bg-dark-900/50 border-dark-700 backdrop-blur-sm hover:shadow-glow transition-all duration-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Target className="h-5 w-5 text-green-500" />
                Top 10 Average
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-400">
                {displayResults?.top_trials
                  ? (() => {
                      const vals = displayResults.top_trials
                        .map((t) => t.value)
                        .filter((v): v is number => typeof v === "number");
                      return vals.length
                        ? (
                            vals.reduce((a, b) => a + b, 0) / vals.length
                          ).toFixed(4)
                        : "N/A";
                    })()
                  : "N/A"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Range: ±
                {displayResults?.top_trials
                  ? (() => {
                      const vals = displayResults.top_trials
                        .map((t) => t.value)
                        .filter((v): v is number => typeof v === "number");
                      if (vals.length < 2) return "N/A";
                      return (Math.max(...vals) - Math.min(...vals)).toFixed(4);
                    })()
                  : "N/A"}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl blur-lg group-hover:blur-md transition-all" />
          <Card className="relative bg-dark-900/50 border-dark-700 backdrop-blur-sm hover:shadow-glow transition-all duration-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Zap className="h-5 w-5 text-purple-500" />
                Total Trials
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-purple-400">
                {displayResults.n_trials}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Best trials analyzed below
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* AI-Powered Insights - Below key metrics */}
      <AIInsights
        studyId={studyId || ""}
        studyName={displayResults?.study_name}
        trials={
          displayResults?.top_trials?.map((trial) => ({
            ...trial,
            state: "COMPLETE" as const,
          })) || []
        }
        onAnalysisGenerated={(analysis: AIAnalysisResult) => {}}
      />

      {/* Study Configuration Panel */}
      <StudyConfigurationPanel
        studyName={displayResults.study_name}
        symbol={displayResults.symbol}
        timeframe={displayResults.timeframe}
        parameters={displayResults.parameters}
        createdAt={displayResults.created_at}
        completedAt={displayResults.completed_at}
        nTrials={displayResults.n_trials}
        tomlConfig={displayResults.toml_config}
      />

      {/* Main Analysis Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="patterns">Patterns</TabsTrigger>
          <TabsTrigger value="parameters">Parameters</TabsTrigger>
          <TabsTrigger value="convergence">Convergence</TabsTrigger>
          <TabsTrigger value="recommendations">Insights</TabsTrigger>
        </TabsList>

        {/* Overview Tab - Focus on Top 10 Results */}
        <TabsContent value="overview" className="space-y-6">
          {/* Optimization History Chart */}
          {displayResults && (
            <OptimizationHistoryChart
              results={displayResults}
              className="mb-6"
              pointLimit={pointLimit}
              onPointLimitChange={setPointLimit}
              totalTrialsCount={fullResults?.top_trials?.length || 0}
            />
          )}

          {/* Top 10 Results with Settings */}
          <Card className="bg-dark-900/50 border-dark-700 hover:shadow-glow transition-all duration-300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Top {topResultsCount} Results with Settings
              </CardTitle>
              <CardDescription>
                Best performing parameter combinations ranked by performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {topTrialsToRender.map((trial, index) => (
                  <div
                    key={trial.number}
                    className={cn(
                      "p-4 rounded-xl border transition-all duration-300 hover:shadow-lg",
                      index === 0
                        ? "bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30 shadow-glow"
                        : index === 1
                          ? "bg-gradient-to-r from-gray-300/15 to-gray-500/15 border-gray-300/40 shadow-sm"
                          : index === 2
                            ? "bg-gradient-to-r from-amber-600/10 to-amber-800/10 border-amber-600/30"
                            : "bg-dark-800/30 border-dark-600 hover:border-dark-500",
                    )}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {index === 0 && (
                          <div className="h-6 w-6 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-xs font-bold text-white shadow-lg ring-2 ring-yellow-300/50">
                            1
                          </div>
                        )}
                        {index === 1 && (
                          <div className="h-6 w-6 rounded-full bg-gradient-to-br from-gray-300 to-gray-500 flex items-center justify-center text-xs font-bold text-white shadow-lg">
                            2
                          </div>
                        )}
                        {index === 2 && (
                          <div className="h-6 w-6 rounded-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center text-xs font-bold text-white">
                            3
                          </div>
                        )}
                        {index > 2 && (
                          <div className="h-6 w-6 rounded-full border border-dark-500 flex items-center justify-center text-xs font-bold text-gray-400">
                            {index + 1}
                          </div>
                        )}
                        <div>
                          <h3 className="font-bold text-lg">
                            Trial #{trial.number}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Rank {index + 1}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold font-mono text-brand-blue">
                          {formatPerformanceValue(trial.value as number)}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {(() => {
                            const dur = getTrialDurationSeconds(
                              trial as unknown as Record<string, unknown>,
                            );
                            return dur != null ? `${dur.toFixed(1)}s` : "N/A";
                          })()}
                        </p>
                      </div>
                    </div>

                    {/* Parameter Settings Grid - Grouped by Category */}
                    {(() => {
                      // Filter out dependent params when their controlling filter is disabled
                      const filteredParams = filterDisabledDependentParams(
                        trial.params,
                      );
                      const groupedParams = groupParametersByCategory(
                        filteredParams,
                      );
                      const categories = getCategoryDisplayOrder();

                      return (
                        <div className="space-y-6">
                          {categories.map((category) => {
                            const params = groupedParams[category];
                            if (!params || params.length === 0) return null;

                            return (
                              <div key={category} className="space-y-3">
                                <h4 className="text-sm font-semibold text-blue-400 border-b border-blue-500/30 pb-1">
                                  {category}
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                  {params.map(([key, value]) => {
                                    const description =
                                      getParameterDescription(key);
                                    return (
                                      <TooltipProvider key={key}>
                                        <UITooltip>
                                          <TooltipTrigger asChild>
                                            <div className="bg-dark-800/50 rounded-lg p-3 border border-dark-700 cursor-help hover:border-dark-600 transition-colors">
                                              <p className="text-xs text-muted-foreground font-medium mb-1">
                                                {getParameterDisplayName(key)}
                                              </p>
                                              <div
                                                className="font-mono text-sm font-bold"
                                                dangerouslySetInnerHTML={{
                                                  __html: formatParameterValue(
                                                    key,
                                                    value,
                                                  ),
                                                }}
                                              />
                                            </div>
                                          </TooltipTrigger>
                                          {description && (
                                            <TooltipContent
                                              side="top"
                                              className="max-w-xs"
                                            >
                                              <p className="text-sm">
                                                {description}
                                              </p>
                                            </TooltipContent>
                                          )}
                                        </UITooltip>
                                      </TooltipProvider>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Parameters Tab */}
        <TabsContent value="parameters" className="space-y-4">
          {/* Enhanced Parameter Importance Analysis */}
          {results && analysis && (
            <ParameterImportanceEnhanced
              results={results}
              analysis={analysis}
            />
          )}

          <Card>
            <CardHeader>
              <CardTitle>Parameter Statistics</CardTitle>
              <CardDescription>
                Detailed statistics for each parameter across top trials
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Parameter</th>
                      <th className="text-right p-2">Min</th>
                      <th className="text-right p-2">Max</th>
                      <th className="text-right p-2">Average</th>
                      <th className="text-right p-2">Std Dev</th>
                      <th className="text-right p-2">Convergence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analysis &&
                      Object.entries(analysis.paramStats).map(
                        ([name, stats]) => (
                          <tr key={name} className="border-b hover:bg-muted/50">
                            <td className="p-2 font-medium">
                              {getParameterDisplayName(name)}
                            </td>
                            <td className="p-2 text-right font-mono">
                              {stats.min.toFixed(2)}
                            </td>
                            <td className="p-2 text-right font-mono">
                              {stats.max.toFixed(2)}
                            </td>
                            <td className="p-2 text-right font-mono">
                              {stats.avg.toFixed(2)}
                            </td>
                            <td className="p-2 text-right font-mono">
                              {stats.stdDev.toFixed(2)}
                            </td>
                            <td className="p-2 text-right">
                              <Badge
                                variant={
                                  stats.convergenceScore < 0.3
                                    ? "secondary"
                                    : "default"
                                }
                              >
                                {(stats.convergenceScore * 100).toFixed(1)}%
                              </Badge>
                            </td>
                          </tr>
                        ),
                      )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Convergence Tab */}
        <TabsContent value="convergence" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Performance Convergence</CardTitle>
              <CardDescription>
                How quickly the optimization found good solutions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={
                      analysis?.performanceProgression?.sort(
                        (a, b) => b.rank - a.rank,
                      ) || []
                    }
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="rank"
                      label={{
                        value: "Rank",
                        position: "insideBottom",
                        offset: -5,
                      }}
                    />
                    <YAxis
                      label={{
                        value: "Improvement %",
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <Tooltip
                      formatter={(value: number | string) =>
                        `${Number(value).toFixed(2)}%`
                      }
                    />
                    <Line
                      type="monotone"
                      dataKey="improvement"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={{ fill: "#3b82f6", r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Convergence Metrics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium">
                      Overall Convergence
                    </span>
                    <span className="text-sm font-bold">
                      {analysis
                        ? (analysis.convergenceRate * 100).toFixed(1)
                        : "0"}
                      %
                    </span>
                  </div>
                  <Progress
                    value={analysis ? analysis.convergenceRate * 100 : 0}
                  />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium">
                      Top 10 Consistency
                    </span>
                    <span className="text-sm font-bold">
                      {results?.top_trials
                        ? (() => {
                            const vals = results.top_trials
                              .map((t) => t.value)
                              .filter(
                                (v): v is number => typeof v === "number",
                              );
                            if (vals.length < 2) return "0";
                            const spread =
                              Math.max(...vals) - Math.min(...vals);
                            const denom = Math.abs(results.best_value || 1);
                            return ((1 - spread / denom) * 100 || 0).toFixed(1);
                          })()
                        : "0"}
                      %
                    </span>
                  </div>
                  <Progress
                    value={(() => {
                      if (!results?.top_trials) return 0;
                      const vals = results.top_trials
                        .map((t) => t.value)
                        .filter((v): v is number => typeof v === "number");
                      if (vals.length < 2) return 0;
                      const spread = Math.max(...vals) - Math.min(...vals);
                      const denom = Math.abs(results.best_value || 1);
                      return Math.max(
                        0,
                        Math.min(100, (1 - spread / denom) * 100),
                      );
                    })()}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Trial Efficiency</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      Total Trials
                    </span>
                    <span className="font-medium">
                      {results?.n_trials || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      Trials to Best
                    </span>
                    <span className="font-medium">
                      {results?.best_trial?.number || "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      Top 10 Found By
                    </span>
                    <span className="font-medium">
                      Trial{" "}
                      {results?.top_trials
                        ? Math.max(...results.top_trials.map((t) => t.number))
                        : "N/A"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">
                      Efficiency Score
                    </span>
                    <Badge>
                      {results
                        ? (
                            ((results.best_trial?.number || 0) /
                              results.n_trials) *
                            100
                          ).toFixed(1)
                        : "0"}
                      %
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Patterns Tab */}
        <TabsContent value="patterns" className="space-y-4">
          <div className="grid grid-cols-1 gap-6">
            {/* Parameter Landscape (2D Contour) - Full Width */}
            <ParameterContourPlotLazy
              results={displayResults}
              onParameterSelect={(xParam, yParam) => {}}
            />

            {/* 3D Parameter Space Explorer — gated behind expand toggle (AIE-298) */}
            <Card>
              <CardHeader
                className="cursor-pointer select-none"
                onClick={() => setShow3DExplorer((prev) => !prev)}
              >
                <CardTitle className="flex items-center gap-2">
                  {show3DExplorer ? (
                    <ChevronDown className="h-5 w-5" />
                  ) : (
                    <ChevronRight className="h-5 w-5" />
                  )}
                  <Box className="h-5 w-5" />
                  3D Parameter Space Explorer
                </CardTitle>
                <CardDescription>
                  Visualize how the optimizer explored the parameter space
                  across all trials
                </CardDescription>
              </CardHeader>
              {show3DExplorer && (
                <CardContent className="space-y-4">
                  <ParameterExplorer3DLazy studyId={studyId} />
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate(`/results/compare?a=${studyId}`, {
                          state: { lockedA: true },
                        })
                      }
                    >
                      Compare with another study
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Two-column layout for additional pattern analysis */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Parameter Convergence Patterns */}
              <Card>
                <CardHeader>
                  <CardTitle>Parameter Convergence Patterns</CardTitle>
                  <CardDescription>
                    How parameters converged during optimization
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart
                        data={
                          analysis
                            ? Object.entries(analysis.paramStats)
                                .filter(([name]) => {
                                  // Filter out boolean parameters (Use ___ Filter, Use ___ Exits)
                                  if (isBooleanParameter(name)) return false;

                                  // Filter out minimum trades parameter
                                  const nameLower = name.toLowerCase();
                                  if (
                                    nameLower.includes("minimum") &&
                                    nameLower.includes("trade")
                                  ) {
                                    return false;
                                  }

                                  return true;
                                })
                                .map(([name, stats]) => ({
                                  parameter: getParameterDisplayName(name),
                                  optimal:
                                    ((stats.avg - stats.min) /
                                      (stats.max - stats.min)) *
                                    100,
                                  spread: (1 - stats.convergenceScore) * 100,
                                }))
                            : []
                        }
                      >
                        <PolarGrid />
                        <PolarAngleAxis dataKey="parameter" />
                        <PolarRadiusAxis angle={90} domain={[0, 100]} />
                        <Radar
                          name="Convergence %"
                          dataKey="spread"
                          stroke="#10b981"
                          fill="#10b981"
                          fillOpacity={0.4}
                        />
                        <Radar
                          name="Optimal Range %"
                          dataKey="optimal"
                          stroke="#3b82f6"
                          fill="#3b82f6"
                          fillOpacity={0.7}
                        />
                        <Legend />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Top Parameter Combinations */}
              <Card>
                <CardHeader>
                  <CardTitle>Top Parameter Combinations</CardTitle>
                  <CardDescription>
                    Most successful parameter combinations
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {displayResults?.top_trials
                      ?.slice(0, 3)
                      .map((trial, index) => (
                        <div
                          key={trial.number}
                          className="border rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              {index === 0 && (
                                <div className="h-5 w-5 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-xs font-bold text-white shadow ring-2 ring-yellow-300/50">
                                  1
                                </div>
                              )}
                              {index === 1 && (
                                <div className="h-5 w-5 rounded-full bg-gradient-to-br from-gray-300 to-gray-500 flex items-center justify-center text-xs font-bold text-white shadow">
                                  2
                                </div>
                              )}
                              {index === 2 && (
                                <div className="h-5 w-5 rounded-full bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center text-xs font-bold text-white">
                                  3
                                </div>
                              )}
                              <h4 className="font-medium">
                                Rank {index + 1} - Trial {trial.number}
                              </h4>
                            </div>
                            <Badge variant="outline">
                              {Number(trial.value).toFixed(4)}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {Object.entries(filterDisabledDependentParams(trial.params))
                              .slice(0, 4)
                              .map(([key, value]) => (
                                <div
                                  key={key}
                                  className="bg-muted rounded px-3 py-2"
                                >
                                  <p className="text-xs text-muted-foreground">
                                    {getParameterDisplayName(key)}
                                  </p>
                                  <div
                                    className="font-mono text-sm"
                                    dangerouslySetInnerHTML={{
                                      __html: formatParameterValue(key, value),
                                    }}
                                  />
                                </div>
                              ))}
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5" />
                Optimization Insights
              </CardTitle>
              <CardDescription>
                AI-powered analysis and recommendations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Key Parameters */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Critical Parameters
                </h4>
                <div className="space-y-2">
                  {analysis?.parameterImportance
                    ?.slice(0, 3)
                    .map((param, index) => (
                      <div
                        key={param.name}
                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                      >
                        <div>
                          <p className="font-medium">
                            {getParameterDisplayName(param.name)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Optimal range:{" "}
                            {analysis?.paramStats[param.name]?.min.toFixed(2) ??
                              "N/A"}{" "}
                            -{" "}
                            {analysis?.paramStats[param.name]?.max.toFixed(2) ??
                              "N/A"}
                          </p>
                        </div>
                        <Badge variant={index === 0 ? "default" : "secondary"}>
                          {(param.importance * 100).toFixed(0)}% impact
                        </Badge>
                      </div>
                    ))}
                </div>
              </div>

              {/* Recommendations */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Recommendations
                </h4>
                <div className="space-y-3">
                  {displayAnalysis &&
                    generateRecommendations(displayAnalysis).map(
                      (rec, index) => (
                        <div
                          key={index}
                          className="flex gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg"
                        >
                          <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                          <p className="text-sm">{rec}</p>
                        </div>
                      ),
                    )}
                </div>
              </div>

              {/* Next Steps */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <ArrowUpRight className="h-4 w-4" />
                  Suggested Next Steps
                </h4>
                <div className="space-y-2">
                  <div className="p-3 border rounded-lg">
                    <p className="text-sm">
                      <span className="font-medium">
                        1. Fine-tune critical parameters:
                      </span>{" "}
                      Focus on{" "}
                      {analysis?.parameterImportance
                        ?.slice(0, 2)
                        .map((p) => getParameterDisplayName(p.name))
                        .join(" and ") || "key parameters"}{" "}
                      within their optimal ranges for maximum impact.
                    </p>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <p className="text-sm">
                      <span className="font-medium">2. Validate results:</span>{" "}
                      Test the top 3 parameter combinations on out-of-sample
                      data to ensure robustness.
                    </p>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <p className="text-sm">
                      <span className="font-medium">
                        3. Consider ensemble approach:
                      </span>{" "}
                      Combine insights from multiple top-performing trials for a
                      more robust strategy.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Main component that routes between library and detail views
export function ResultsEnhanced() {
  const { studyId: urlStudyId } = useParams<{ studyId: string }>();

  if (urlStudyId) {
    // If we have a study ID, show the detailed view
    return <StudyResultsDetail studyId={urlStudyId} />;
  } else {
    // Otherwise, show the Study Library view
    return <StudyLibrary />;
  }
}
